// @ts-strict-ignore
import { Injectable, Component, OnDestroy } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { UserAttention } from '../model/enums/user-attention';
import { isNeedUserAttention } from '../functions/type-guards/is-need-user-attention';

interface ComponentWithObservableAttention extends Component {
  userAttentionNeeded$: Observable<UserAttention>;
}

type ActiveComponent = ComponentWithObservableAttention | Component;

@Injectable({
  providedIn: 'root',
})
export class ActiveMainComponentInfoService implements OnDestroy {
  activeComponent: ActiveComponent;

  private _destroy$ = new Subject();
  private _currentUserAttention: UserAttention = UserAttention.None;

  ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

  public get userAttention(): UserAttention {
    if (isNeedUserAttention(this.activeComponent) && !this._hasObservableUserAttention(this.activeComponent)) {
      // If the active component has userAttentionNeeded as a non-observable
      return this.activeComponent.userAttentionNeeded;
    }
    // Return the value from the observable or default to UserAttention.None
    return this._currentUserAttention;
  }

  public get warnOnClose(): boolean {
    let userAttention: UserAttention;

    if (isNeedUserAttention(this.activeComponent) && !this._hasObservableUserAttention(this.activeComponent)) {
      // If the active component has userAttentionNeeded as a non-observable
      userAttention = this.activeComponent.userAttentionNeeded;
    } else {
      // Use the value from the observable or default to UserAttention.None
      userAttention = this._currentUserAttention;
    }

    const shouldWarn = userAttention === UserAttention.UnsavedData || userAttention === UserAttention.NonSavableData;
    return shouldWarn;
  }

  public registerActiveComponent(component: ActiveComponent): void {
    this.activeComponent = component;

    // Reset the current user attention
    this._currentUserAttention = UserAttention.None;

    // Check if the component has a userAttentionNeeded$ property and it's an Observable
    if (this._hasObservableUserAttention(this.activeComponent)) {
      this.activeComponent.userAttentionNeeded$.pipe(takeUntil(this._destroy$)).subscribe((userAttention) => {
        // Update the current user attention
        this._currentUserAttention = userAttention;
      });
    }
  }

  private _hasObservableUserAttention(component: ActiveComponent): component is ComponentWithObservableAttention {
    return 'userAttentionNeeded$' in component;
  }
}
